import Service, { type Registry as Services, service } from '@ember/service';
import dayjs from 'dayjs';
import {
  BENI_COMPARISON_SETS,
  type ComparisonSet,
} from 'frontend-utils/utils/constants/benchmark-insight';
import type { BeniMetaComparisonSet } from 'lumen/models';
import { BENI_COMPARISON_SET_LONG_TRANSLATIONS } from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import type { DataSyncStatusItem } from 'lumen/utils/constants/benchmark-insight/beni-data-sync-constant-values';

export default class BenchmarkInsightBeniDataSyncServiceService extends Service {
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];
  @service declare intl: Services['intl'];

  filterMarketClass(
    comparisonSets: BeniMetaComparisonSet[],
  ): BeniMetaComparisonSet[] {
    const { MARKET_COLLAPSED_CLASS, MARKET_CLASS } = BENI_COMPARISON_SETS;

    const marketComparisonSets = comparisonSets.filter(
      (comparisonSet) =>
        comparisonSet.comparisonSetType === MARKET_COLLAPSED_CLASS ||
        comparisonSet.comparisonSetType === MARKET_CLASS,
    );

    let result = marketComparisonSets.find(
      (comparisonSet) =>
        comparisonSet.comparisonSetType === MARKET_CLASS &&
        comparisonSet.extractDate !== null,
    );

    if (!result) {
      result =
        marketComparisonSets.find(
          (comparisonSet) => comparisonSet.extractDate !== null,
        ) ||
        marketComparisonSets.find(
          (comparisonSet) => comparisonSet.comparisonSetType === MARKET_CLASS,
        );
    }

    const typeToFilter =
      result?.comparisonSetType === MARKET_CLASS
        ? MARKET_COLLAPSED_CLASS
        : MARKET_CLASS;
    return comparisonSets.filter(
      (comparisonSet) => comparisonSet.comparisonSetType !== typeToFilter,
    );
  }

  mapComparisonSetsToDataSyncList(
    comparisonSets: BeniMetaComparisonSet[],
  ): DataSyncStatusItem[] {
    const { allowedComparisonSets, lastPastDate } =
      this.benchmarkInsightBeniService;

    const comparisonSetOrder: ComparisonSet[] = [
      BENI_COMPARISON_SETS.OWN,
      ...allowedComparisonSets,
    ];

    const orderedSets = comparisonSets
      .map(({ comparisonSetType, extractDate }) => ({
        comparisonSetType,
        extractDate,
      }))
      .sort(
        (a, b) =>
          comparisonSetOrder.indexOf(a.comparisonSetType) -
          comparisonSetOrder.indexOf(b.comparisonSetType),
      );

    const list: DataSyncStatusItem[] = [];

    orderedSets.forEach((comparisonSet) => {
      const { comparisonSetType, extractDate } = comparisonSet;

      const extractDateDayjs = dayjs.utc(extractDate);
      let isSynced = extractDateDayjs.isSame(lastPastDate, 'day');

      const name = this.intl.t(
        BENI_COMPARISON_SET_LONG_TRANSLATIONS[comparisonSetType],
      );

      if (extractDate === null) {
        isSynced = false;
      }

      list.push({
        name,
        extractDate,
        isSynced,
        comparisonSetType,
      });
    });

    return list;
  }

  oldestExtractDateDayDiff(extractDates: (string | null)[]): number {
    const validExtractDates = extractDates.filter(
      (extractDate) => extractDate !== null,
    );
    const dates = validExtractDates.map((extractDate) =>
      dayjs.utc(extractDate).startOf('day'),
    );
    const oldestDate = dayjs.min(dates);

    if (!oldestDate) {
      return 0;
    }

    const today = dayjs.utc().startOf('day');
    const correctedOldestDate = oldestDate.add(1, 'day'); // Add 1 day because data from yesterday's extract date is considered up to date today.
    const diff = today.diff(correctedOldestDate, 'day');

    return diff;
  }
}

declare module '@ember/service' {
  interface Registry {
    'benchmark-insight/beni-data-sync-service': BenchmarkInsightBeniDataSyncServiceService;
  }
}
